import dayjs, { type Dayjs } from "dayjs"

import { DATE_FORMAT } from "../../../../utils/date/date.ts"
import { ALL_DATE_FORMATS } from "../../../TeamManagementPage/constants/date-formats.ts"

/** One wall-clock space for sorting, filtering and display. */
const WALL_CLOCK = "YYYY-MM-DDTHH:mm:ss"

const IS_ISO_SHAPED = /^\d{4}-\d{2}-\d{2}T/

/**
 * A zone designator is the only thing that makes a reading an instant. Without
 * one, an ISO-shaped string is a wall clock — including `WALL_CLOCK` itself,
 * which every composed entry carries and the timeline reads back.
 */
const HAS_ZONE_DESIGNATOR = /(?:Z|[+-]\d{2}:?\d{2})$/i

/**
 * Lead payloads and audit rows arrive already converted to the user's timezone
 * and date format — re-converting them shifts the reading a second time — while
 * `created` and the nested calendar fields are true ISO instants. Both are
 * normalised to the user's wall clock here so one comparison works across all
 * of them.
 */
function parseActivityMoment(
  value: unknown,
  userFormat?: string | null,
  timezone?: string | null
): Dayjs | null {
  if (value == null) return null

  const raw = String(value).trim()
  if (!raw) return null

  if (HAS_ZONE_DESIGNATOR.test(raw)) {
    const instant = timezone ? dayjs.utc(raw).tz(timezone) : dayjs(raw)
    return instant.isValid() ? instant : null
  }

  if (IS_ISO_SHAPED.test(raw)) {
    const wallClock = dayjs(raw)
    return wallClock.isValid() ? wallClock : null
  }

  const formats = userFormat
    ? [
        `${userFormat} HH:mm`,
        `${userFormat} hh:mm A`,
        `${userFormat} h:mm A`,
        userFormat,
        ...ALL_DATE_FORMATS
      ]
    : ALL_DATE_FORMATS

  // Non-strict takes the first format that parses at all, so "02:30 PM" would
  // land on "HH:mm" and lose the meridiem; strict picks the format that fits.
  const strict = dayjs(raw, formats, true)
  if (strict.isValid()) return strict

  const lenient = dayjs(raw, formats, false)
  if (lenient.isValid()) return lenient

  const loose = dayjs(raw)
  return loose.isValid() ? loose : null
}

const toWallClock = (
  value: unknown,
  userFormat?: string | null,
  timezone?: string | null
): string | null =>
  parseActivityMoment(value, userFormat, timezone)?.format(WALL_CLOCK) ?? null

const formatActivityMoment = (
  value: unknown,
  userFormat?: string | null,
  timezone?: string | null
): string =>
  parseActivityMoment(value, userFormat, timezone)?.format(
    `${userFormat || DATE_FORMAT} h:mm A`
  ) ?? ""

export { WALL_CLOCK, formatActivityMoment, parseActivityMoment, toWallClock }
